import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Create, Edit, useNotify, useRefresh, useTranslate } from 'react-admin';
import { DialogFormProvider } from '@/components/forms';

export interface ResourceFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  resource: string;
  recordId?: string | number;
  title: string;
  createRecord?: object;
  createdMessage?: string;
  updatedMessage?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  transform?: (data: any) => any | Promise<any>;
  queryOptions?: Record<string, any>;
  children: React.ReactNode;
}

export const ResourceFormDialog: React.FC<ResourceFormDialogProps> = ({
  open,
  onClose,
  mode,
  resource,
  recordId,
  title,
  createRecord,
  createdMessage,
  updatedMessage,
  maxWidth = 'lg',
  transform,
  queryOptions,
  children,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const onSuccess = (): void => {
    const key = mode === 'edit' ? updatedMessage : createdMessage;
    if (key) notify(key, { type: 'success' });
    refresh();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth={maxWidth}
      keepMounted={false}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',

          alignItems: 'center',
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(title, { _: title })}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: '76px',
        }}
      >
        <DialogFormProvider onClose={onClose}>
          <Box
            sx={{
              px: { xs: 1.5, sm: 4 },
              py: { xs: 2, sm: 4 },
              '& .RaCreate-main, & .RaEdit-main, & .MuiCard-root': {
                boxShadow: 'none',
                background: 'transparent',
                margin: 0,
                border: 'none',
              },
              '& .MuiToolbar-root': {
                position: 'absolute !important' as any,
                bottom: 0,
                left: 0,
                right: 0,
                margin: '0 !important',
                borderTop: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',    
                zIndex: 10,
                boxShadow: 'none !important',
              },
            }}
          >
            {mode === 'create' ? (
              <Create
                resource={resource}
                record={createRecord}
                mutationOptions={{ onSuccess }}
                redirect={false}
                actions={false}
                transform={transform}
              >
                {children}
              </Create>
            ) : (
              <Edit
                resource={resource}
                id={recordId}
                mutationMode="pessimistic"
                mutationOptions={{ onSuccess }}
                redirect={false}
                actions={false}
                transform={transform}
                queryOptions={queryOptions}
              >
                {children}
              </Edit>
            )}
          </Box>
        </DialogFormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceFormDialog;
